import { Lock } from 'lucide-react';
import { Button, Typography } from '../ui';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { laneLabel, renderLaneLine, type WireOwnerEvent } from '../../../lib/outreach-lane';
import { useAgentName } from '../../../lib/use-agent-name';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useTranslation } from '../../../lib/i18n';
import styles from './outreach.module.css';

/**
 * One owner event in a thread's transcript — "You told {agent}: …", "You chose …", "You approved" — drawn
 * from `laneLabel` (`lib/outreach-lane.ts`, the shared contract). Centred and muted, with no bubble: it is
 * something the owner did, not a message anybody sent.
 *
 * A note still PENDING after two minutes offers the conversation it was handed to ("Open chat"), so the
 * owner can see what their agent made of it. The stale rule reads the clock at render — there is no
 * timer of its own.
 *
 * `privateNote` (the speaker model's `privateNote` row, a TOLD note the agent did not send verbatim): ONE
 * private row — a lock and "To {agent} · only you see this" as its header, the owner's words under it, then
 * the note's state when it has one. It replaces the "Only you see this" caption + "You told {agent}: …" pair.
 */
export function OutreachLaneItem({
  event,
  outreachId,
  page,
  privateNote = false,
}: {
  event: WireOwnerEvent;
  outreachId: string;
  page: string;
  privateNote?: boolean;
}) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const line = laneLabel(event, new Date());
  if (!line) return null;
  const tr = t as (key: string, params?: Record<string, unknown>) => string;
  const action = line.action;
  return (
    <div className={styles.laneItem} data-testid={privateNote ? 'outreach-private-note' : undefined}>
      {privateNote ? (
        <>
          <span className={styles.laneHeader}>
            <Lock size={12} aria-hidden />
            <Typography variant="p3" color="secondary">
              {t('outreach.thread.privateNote', { maestroAgentName: agent.name })}
            </Typography>
          </span>
          <Typography variant="p3" color="secondary" className={styles.laneText} dir="auto">
            {line.suffix ? `${event.text?.trim() ?? ''} · ${tr(line.suffix, { maestroAgentName: agent.name })}` : event.text?.trim()}
          </Typography>
        </>
      ) : (
        <Typography variant="p3" color="secondary" className={styles.laneText} dir="auto">
          {renderLaneLine(line, tr, agent.name)}
        </Typography>
      )}
      {action ? (
        <Button
          variant="text"
          size="tiny"
          href={chatHandoffLink({ kind: 'existing', conversationId: action.conversationId, message: '' }, basePath).route}
          onClick={() => logPageClickBi({ page_name: page, button_name: 'open_chat_from_note' }, { additionalFields: { outreach_id: outreachId } })}
        >
          {t(action.labelKey)}
        </Button>
      ) : null}
    </div>
  );
}
